"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { FaPaperPlane, FaChalkboardTeacher, FaUser, FaSpinner, FaPaperclip, FaRedo, FaStopCircle } from "react-icons/fa";

import AITutorNavbar from "@/app/ai-tutor/components/AITutorNavbar";
import ChatSidebar from "@/app/ai-tutor/components/ChatSidebar";
import FileAttachment from "@/app/ai-tutor/components/FileAttachment";
import { API_ENDPOINTS } from "@/config/api-endpoints-config";
import MarkdownMathRenderer from '@/components/common/MarkdownMathRenderer';
import TextareaAutosize from 'react-textarea-autosize';

const AIResponseRenderer = ({ content }) => <MarkdownMathRenderer content={content} />;

const parseApiError = async (response, defaultMessage) => {
  if (response.status === 429) {
    return "You've reached your daily limit for AI chat messages. Please try again tomorrow.";
  }
  try {
    const errorData = await response.text();
    try {
      const jsonError = JSON.parse(errorData);
      return jsonError.detail || jsonError.message || defaultMessage;
    } catch {
      return errorData || defaultMessage;
    }
  } catch {
    return defaultMessage;
  }
};

const AITutorComponent = ({ chatId: urlChatId = null }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();

  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(urlChatId);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authError, setAuthError] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [abortController, setAbortController] = useState(null);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastLoadedChatIdRef = useRef(null);

  // --- START OF REACTIVE URL CLEANUP ---
  useEffect(() => {
    const errorParam = searchParams.get('error');

    if (errorParam) {
      // Set the beautiful UI error message
      if (errorParam === 'Callback' || errorParam === 'AccessDenied') {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setAuthError('Authentication was cancelled or failed. Please try again.');
      } else {
        setAuthError('An error occurred during login. Please try again.');
      }

      // Use Next.js router to securely wipe the URL parameters
      router.replace('/ai-tutor', { scroll: false });
    }
  }, [searchParams, router]);
  // --- END OF REACTIVE URL CLEANUP ---


  // Automatically open the sidebar if the user is on a desktop
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSidebarOpen(true);
    }
  }, []);

  // Lock body scroll and entirely prevent scroll chaining to keep address bar visible
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  // Add CSS to constrain KaTeX elements
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .katex-display {
        margin: 0.5em 0 !important;
        overflow-x: auto !important;
        max-width: 100% !important;
        position: relative !important;
      }
      .katex {
        font-size: 1em !important;
        position: relative !important;
      }
      .katex-html {
        position: relative !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Redirect to centralized login if unauthenticated
  useEffect(() => {
    if (status === "unauthenticated") {
      const currentUrl = window.location.pathname + window.location.search;
      router.push(`/login?returnUrl=${encodeURIComponent(currentUrl)}`);
    }
  }, [status, router]);

  // Load chat list once authenticated
  useEffect(() => {
    if (status === "authenticated") {
      // eslint-disable-next-line react-hooks/immutability
      loadChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Sync active chat and messages with URL
  useEffect(() => {
    if (status !== "authenticated") return;

    if (!urlChatId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveChatId(null);
      setMessages([]);
      setAttachedFiles([]);
      lastLoadedChatIdRef.current = null;
      return;
    }

    setActiveChatId(urlChatId);

    if (lastLoadedChatIdRef.current === urlChatId) return;

    lastLoadedChatIdRef.current = urlChatId;

    // eslint-disable-next-line react-hooks/immutability
    loadChatMessages(urlChatId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChatId, status]);

  // Note: We use credentials 'include' so Next.js rewrites forward our cookies automatically
  const resolveAttachedFiles = async (attachedFileRefs) => {
    if (!attachedFileRefs || attachedFileRefs.length === 0) return [];

    const firstFile = attachedFileRefs[0];
    if (typeof firstFile !== 'string') return attachedFileRefs;

    try {
      const listResponse = await fetch(API_ENDPOINTS.FILES.LIST, {
        credentials: 'include'
      });

      if (listResponse.ok) {
        const listData = await listResponse.json();
        return attachedFileRefs.map((fileId) => {
          const fileData = listData?.find((file) => file.id === fileId);
          return fileData || {
            id: fileId,
            original_filename: `File ${fileId}`,
            file_size: 0,
            mime_type: 'application/octet-stream',
            file_type: 'other',
          };
        });
      }
    } catch (error) {
      console.error('Error fetching file list:', error);
    }

    return attachedFileRefs.map((fileId) => ({
      id: fileId,
      original_filename: `File ${fileId}`,
      file_size: 0,
      mime_type: 'application/octet-stream',
      file_type: 'other',
    }));
  };

  const convertApiMessagesToUi = async (apiMessages) => {
    const chronological = [...apiMessages].reverse();
    const pairs = await Promise.all(
      chronological.map(async (msg) => {
        const attachedFiles = await resolveAttachedFiles(msg.attached_files);
        const timestamp = new Date(msg.timestamp);

        return [
          {
            id: `user_${msg.id}`,
            text: msg.message,
            sender: "user",
            timestamp,
            isError: false,
            attachedFiles,
          },
          {
            id: `ai_${msg.id}`,
            text: msg.response,
            sender: "ai",
            timestamp,
            isError: false,
          },
        ];
      })
    );
    return pairs.flat();
  };

  const loadChats = async () => {
    setIsLoadingChats(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.CHATS.LIST}?limit=50`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setChats(data.chats || []);
        return data.chats || [];
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setIsLoadingChats(false);
    }
    return [];
  };

  const loadChatMessages = async (chatId) => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.CHATS.MESSAGES(chatId)}?limit=50`, {
        credentials: 'include'
      });

      if (response.status === 404) {
        lastLoadedChatIdRef.current = null;
        router.replace('/ai-tutor');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const uiMessages = await convertApiMessagesToUi(data.messages || []);
        setMessages(uiMessages);
      } else if (response.status === 401) {
        handleLogout();
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Scroll to last user message without animation so it sits at top of viewport
  const scrollToLastUserMessage = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.sender === "user");
    if (lastUserMsg) {
      const element = document.getElementById(`message-${lastUserMsg.id}`);
      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
    }
    // Fallback if no user message is found
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  useEffect(() => {
    scrollToLastUserMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Update document title dynamically based on active chat and prevent Next.js from overwriting it
  useEffect(() => {
    let desiredTitle = "Personalized AI Tutor | JEE Challenger";
    
    if (activeChatId && chats.length > 0) {
      const activeChat = chats.find(c => c.id === activeChatId);
      if (activeChat) {
        desiredTitle = `${activeChat.title} - AI Tutor | JEE Challenger`;
      } else {
        desiredTitle = "AI Tutor Chat | JEE Challenger";
      }
    }

    // Immediately set the title
    document.title = desiredTitle;

    // Next.js fetches the RSC payload for the new page on navigation and applies its static metadata,
    // which overwrites our client-side title a split second later. 
    // This observer watches the <head> tag and forces it back to our desired title if Next.js tries to change it.
    const observer = new MutationObserver(() => {
      if (document.title !== desiredTitle) {
        document.title = desiredTitle;
      }
    });

    // Observe the entire head in case Next.js completely swaps the <title> DOM node
    observer.observe(document.head, { 
      childList: true, 
      subtree: true,
      characterData: true
    });

    return () => observer.disconnect();
  }, [activeChatId, chats]);


  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};

    messages.forEach((message) => {
      const date = new Date(message.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey;
      if (date.toDateString() === today.toDateString()) {
        dateKey = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = "Yesterday";
      } else {
        dateKey = date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    // Sort messages within each group by timestamp
    Object.keys(groups).forEach(dateKey => {
      groups[dateKey].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });

    return groups;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || status !== "authenticated") return;

    const userMessage = {
      id: crypto.randomUUID(),
      text: inputMessage.trim(),
      sender: "user",
      timestamp: new Date(),
      attachedFiles: attachedFiles.length > 0 ? attachedFiles : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) setSidebarOpen(false);

    const fileIds = attachedFiles.map((file) => file.id);
    const controller = new AbortController();
    setAbortController(controller);

    try {
      let response;
      let data;

      if (!activeChatId) {
        response = await fetch(API_ENDPOINTS.CHATS.CREATE, {
          method: "POST",
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userMessage.text,
            file_ids: fileIds.length > 0 ? fileIds : undefined,
          }),
          signal: controller.signal,
        });
      } else {
        response = await fetch(API_ENDPOINTS.CHATS.MESSAGES(activeChatId), {
          method: "POST",
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: userMessage.text,
            file_ids: fileIds.length > 0 ? fileIds : null,
          }),
          signal: controller.signal,
        });
      }

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to get response from AI"));
      }

      data = await response.json();

      if (!activeChatId && data.chat_id) {
        lastLoadedChatIdRef.current = data.chat_id;
        setActiveChatId(data.chat_id);
        setChats((prev) => [
          {
            id: data.chat_id,
            title: data.chat_title || "New Chat",
            updated_at: new Date().toISOString(),
          },
          ...prev.filter((c) => c.id !== data.chat_id),
        ]);
        router.replace(`/ai-tutor/chat/${data.chat_id}`);
      } else if (activeChatId) {
        setChats((prev) =>
          prev.map((c) =>
            c.id === activeChatId
              ? { ...c, updated_at: new Date().toISOString() }
              : c
          ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        );
      }

      const aiMessage = {
        id: `ai_${data.message_id || crypto.randomUUID()}`,
        text: data.response,
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setChats((prev) =>
        prev.map((c) =>
          c.id === activeChatId
            ? { ...c, updated_at: new Date().toISOString() }
            : c
        ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      );
      setAttachedFiles([]);
    } catch (error) {
      // Intercept the intentional abort
      if (error.name === 'AbortError' || error === 'User stopped generation') {
        const stopMessage = {
          id: crypto.randomUUID(),
          text: "Generation stopped.",
          sender: "ai",
          timestamp: new Date(),
          isError: true,
          failedText: userMessage.text,
        };
        setMessages((prev) => [...prev, stopMessage]);
        return;
      }

      console.error("Error sending message:", error);

      let displayMessage = "Sorry, I'm having trouble connecting right now. Please try again in a moment.";
      if (typeof error.message === 'string' && (
        error.message.includes('limit') ||
        error.message.includes('File size') ||
        error.message.includes('Unsupported file type')
      )) {
        displayMessage = error.message;
      }

      const errorMessage = {
        id: crypto.randomUUID(),
        text: displayMessage,
        sender: "ai",
        timestamp: new Date(),
        isError: true,
        failedText: userMessage.text,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isLoading || isUploading || !inputMessage.trim()) return;
      sendMessage();
    }
  };

  const handleNewChat = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) setSidebarOpen(false);
    router.push('/ai-tutor');
    inputRef.current?.focus();
  };

  const handleSelectChat = (chatId) => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) setSidebarOpen(false);
    if (chatId === activeChatId) return;
    router.push(`/ai-tutor/chat/${chatId}`);
  };

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleDeleteChat = async (chatId) => {
    try {
      const response = await fetch(API_ENDPOINTS.CHATS.DELETE(chatId), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setChats((prev) => prev.filter((c) => c.id !== chatId));

        if (activeChatId === chatId) {
          lastLoadedChatIdRef.current = null;
          router.push('/ai-tutor');
        }
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const handleRenameChat = async (chatId, title) => {
    try {
      const response = await fetch(API_ENDPOINTS.CHATS.UPDATE(chatId), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });

      if (response.ok) {
        setChats((prev) =>
          prev.map((c) => (c.id === chatId ? { ...c, title } : c))
        );
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  };

  const handleLoginError = (errorMessage) => {
    setAuthError(errorMessage);
  };

  const handleLogout = async () => {
    // setChats([]);
    // setActiveChatId(null);
    // setMessages([]);
    // setInputMessage("");
    // setIsLoading(false);
    // setIsLoadingChats(false);
    // setIsLoadingMessages(false);
    // // setSidebarOpen(false);
    // setAuthError("");
    // setAttachedFiles([]);
    // setIsUploading(false);
    // setUploadError("");

    // lastLoadedChatIdRef.current = null;

    // Trigger NextAuth signout
    await signOut({ callbackUrl: window.location.pathname + window.location.search });
  };

  const messageGroups = groupMessagesByDate(messages);

  // Sort groups by their earliest message timestamp
  const sortedGroupEntries = Object.entries(messageGroups).sort(([, messagesA], [, messagesB]) => {
    const earliestA = Math.min(...messagesA.map(m => new Date(m.timestamp)));
    const earliestB = Math.min(...messagesB.map(m => new Date(m.timestamp)));
    return earliestA - earliestB;
  });

  // Show loading state while NextAuth determines session status
  if (status === "loading") {
    return (
      <div className="fixed inset-0 h-[100dvh] bg-gray-50 dark:bg-black flex items-center justify-center">
        <FaSpinner className="animate-spin text-blue-500 text-3xl" />
      </div>
    );
  }

  // Show loading spinner while redirecting if unauthenticated
  if (status === "unauthenticated" || !session?.user) {
    return (
      <div className="fixed inset-0 h-[100dvh] bg-gray-50 dark:bg-black flex items-center justify-center">
        <FaSpinner className="animate-spin text-blue-500 text-3xl" />
      </div>
    );
  }

  const handleDirectFileUpload = async (files) => {
    setUploadError("");
    setIsUploading(true);

    const compiledFiles = [];

    try {
      // Loop sequentially or in parallel over each chosen asset to process split transaction states securely
      for (const file of Array.from(files)) {
        // Phase 1: Request upload url negotiation block
        const reqResponse = await fetch(API_ENDPOINTS.FILES.REQUEST_UPLOAD, {
          method: 'POST',
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: file.name,
            mime_type: file.type || 'application/octet-stream',
            file_size: file.size
          })
        });

        if (reqResponse.status === 401) {
          handleLogout();
          return;
        }

        if (!reqResponse.ok) {
          let errorMessage = `HTTP ${reqResponse.status}: ${reqResponse.statusText}`;

          if (reqResponse.status === 429) {
            errorMessage = "You've reached your daily limit for file uploads. Please try again tomorrow.";
          } else if (reqResponse.status === 413) {
            errorMessage = "File size too large. Please choose a smaller file.";
          } else if (reqResponse.status === 400) {
            errorMessage = "Unsupported file type.";
          } else {
            try {
              const errorData = await reqResponse.text();
              try {
                const jsonError = JSON.parse(errorData);
                errorMessage = jsonError.detail || jsonError.message || errorMessage;
              } catch {
                errorMessage = errorData || errorMessage;
              }
            } catch { }
          }

          throw new Error(errorMessage);
        }

        const reqData = await reqResponse.json();
        const { presigned_url, file_path } = reqData;

        // Phase 2: Direct Binary Stream Pipeline to Cloudflare R2 via HTTP PUT Method
        const r2Response = await fetch(presigned_url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          },
          body: file
        });

        if (!r2Response.ok) {
          throw new Error(`Direct upload failed with status code: ${r2Response.status}`);
        }

        // Phase 3: Transaction confirmation commit block indexing back to MongoDB
        const confirmResponse = await fetch(API_ENDPOINTS.FILES.CONFIRM_UPLOAD, {
          method: 'POST',
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            file_path: file_path,
            filename: file.name,
            mime_type: file.type || 'application/octet-stream',
            file_size: file.size
          })
        });

        if (!confirmResponse.ok) {
          throw new Error(`Failed to commit file upload state metadata token.`);
        }

        const confirmData = await confirmResponse.json();
        if (confirmData.files && confirmData.files[0]) {
          compiledFiles.push(confirmData.files[0]);
        }
      }

      setAttachedFiles(prev => [...prev, ...compiledFiles]);
    } catch (error) {
      console.error(error)
      let displayError = 'Failed to upload files';
      if (typeof error.message === 'string' && (
        error.message.includes('limit') ||
        error.message.includes('File size') ||
        error.message.includes('Unsupported file type')
      )) {
        displayError = error.message;
      }
      setUploadError(displayError);
    } finally {
      setIsUploading(false);
    }
  };

  // Restore removeAttachedFile handler
  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  return (
    <div className="fixed inset-0 h-[100dvh] bg-gray-50 dark:bg-black overflow-hidden">
      <div className="w-full h-full flex flex-col">
        <AITutorNavbar
          user={session.user}
          onLogout={handleLogout}
          onToggleSidebar={handleToggleSidebar}
          sidebarOpen={sidebarOpen}
        />

        <div className="flex flex-1 min-h-0">
          <ChatSidebar
            chats={chats}
            activeChatId={activeChatId}
            isLoadingChats={isLoadingChats}
            isOpen={sidebarOpen}
            onToggle={handleToggleSidebar}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            onRenameChat={handleRenameChat}
          />

          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 min-h-0 bg-gray-50 dark:bg-gray-900 scrollbar-thin scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
              <div className="max-w-4xl mx-auto">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-16">
                    <FaSpinner className="animate-spin text-blue-500 mr-2" />
                    <span className="text-gray-500 dark:text-gray-400">Loading messages...</span>
                  </div>
                ) : Object.keys(messageGroups).length === 0 ? (
                  <div className="text-center py-8 sm:py-16">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                      <FaChalkboardTeacher className="text-white text-2xl sm:text-3xl" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                      Welcome to JEE Challenger AI Tutor!
                    </h1>
                    <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-md mx-auto">
                      Your personalized JEE preparation assistant
                    </p>
                    <div className="max-w-2xl mx-auto space-y-4 text-left bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg">
                      <p className="text-gray-700 dark:text-gray-200 text-sm font-semibold">
                        What I can help you with:
                      </p>
                      <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2 ml-4">
                        <li className="flex items-center space-x-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span>Physics, Chemistry, and Mathematics concepts</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span>JEE Main and Advanced preparation strategies</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span>Study material recommendations</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span>Problem-solving techniques</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span>Exam tips and time management</span>
                        </li>
                      </ul>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        Just type your question below and I&lsquo;ll help you excel in your JEE journey!
                      </p>
                      <div className="mt-4 sm:mt-6 space-y-3">
                        <p className="text-gray-700 dark:text-gray-200 text-sm font-medium">
                          Try asking:
                        </p>
                        <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                          {[
                            "What are the important topics for JEE Physics?",
                            "How to solve integration problems?",
                            "Best books for JEE Chemistry",
                            "Time management tips for JEE"
                          ].map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => setInputMessage(suggestion)}
                              className="text-xs sm:text-sm bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-3 sm:px-4 py-2 rounded-full transition-all duration-200 border border-blue-200 dark:border-blue-800"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  sortedGroupEntries.map(([date, dateMessages]) => (
                    <div key={date}>
                      {/* Date Separator */}
                      <div className="flex items-center justify-center my-8">
                        <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {date}
                          </span>
                        </div>
                      </div>

                      {/* Messages for this date */}
                      <div className="space-y-4">
                        {dateMessages.map((message) => (
                          <div
                            key={message.id}
                            id={`message-${message.id}`}
                            className={`flex scroll-mt-8 sm:scroll-mt-12 ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`flex items-start md:space-x-3 w-full md:max-w-[80%] ${message.sender === "user" ? "flex-row-reverse md:space-x-reverse" : ""}`}
                            >
                              {/* Avatar */}
                              <div
                                className={`hidden md:flex w-8 h-8 rounded-full items-center justify-center flex-shrink-0 ${message.sender === "user"
                                  ? "bg-blue-500"
                                  : "bg-gradient-to-r from-blue-500 to-purple-600"
                                  }`}
                              >
                                {message.sender === "user" ? (
                                  <FaUser className="text-white text-sm" />
                                ) : (
                                  <FaChalkboardTeacher className="text-white text-sm" />
                                )}
                              </div>

                              <div className="flex flex-col max-w-full min-w-0">
                                {/* Message Bubble */}
                                <div
                                  className={`px-5 py-4 shadow-sm min-w-0 overflow-hidden rounded-2xl ${message.sender === "user"
                                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-tr-[2px]"
                                    : message.isError
                                      ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-tl-[2px]"
                                      : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 shadow-md rounded-tl-[2px]"
                                    }`}
                                >
                                  {message.sender === "ai" ? (
                                    message.isError ? (
                                      <div className="flex flex-col items-start">
                                        <p className="text-base leading-relaxed whitespace-pre-wrap text-left">
                                          {message.text}
                                        </p>
                                        {message.failedText && (
                                          <button
                                            onClick={() => {
                                              // 1. Remove this error message from the chat
                                              setMessages(prev => prev.filter(m => m.id !== message.id));
                                              // 2. Put their text back into the input box
                                              setInputMessage(message.failedText);
                                              // 3. Focus the input so they can instantly hit enter again
                                              inputRef.current?.focus();
                                            }}
                                            className="mt-3 flex items-center space-x-2 text-sm font-medium bg-red-50 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-800 text-red-700 dark:text-red-200 px-3 py-1.5 rounded-lg transition-colors border border-red-200 dark:border-red-700"
                                          >
                                            <FaRedo className="text-xs" />
                                            <span>Retry Message</span>
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <AIResponseRenderer content={message.text} />
                                    )
                                  ) : (
                                    <p className="text-base leading-relaxed whitespace-pre-wrap text-left">
                                      {message.text}
                                    </p>
                                  )}

                                  {/* Display attached files in user messages */}
                                  {message.sender === "user" && message.attachedFiles && message.attachedFiles.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                      {/* <p className="text-xs opacity-80">Attached files:</p> */}
                                      {message.attachedFiles.map((file) => (
                                        <FileAttachment
                                          key={file.id}
                                          file={file}
                                          showRemove={false}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {/* Timestamp */}
                                <p
                                  className={`text-xs whitespace-nowrap mt-1 ${message.sender === "user"
                                    ? "text-blue-500 dark:text-blue-400 text-right"
                                    : message.isError
                                      ? "text-red-600 dark:text-red-400"
                                      : "text-gray-500 dark:text-gray-400"
                                    }`}
                                >
                                  {message.timestamp.toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}

                {/* Loading indicator - AI is thinking... */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start md:space-x-3 w-full max-w-[80%]">
                      <div className="hidden md:flex w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full items-center justify-center">
                        <FaChalkboardTeacher className="text-white text-sm" />
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 rounded-2xl rounded-tl-[2px] flex flex-col items-start">
                        <div className="flex items-center space-x-2">
                          <FaSpinner className="text-blue-500 animate-spin" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            AI is thinking...
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input Area */}
            <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg">
              <div className="max-w-4xl mx-auto">
                {/* Attached Files Display */}
                {attachedFiles.length > 0 && (
                  <div className="mb-3 space-y-1">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Attached files ({attachedFiles.length}):
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
                      {attachedFiles.map((file) => (
                        <FileAttachment
                          key={file.id}
                          file={file}
                          onRemove={removeAttachedFile}
                          showRemove={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploading animation above textarea */}
                {isUploading && (
                  <div className="flex items-center justify-center mb-1">
                    <FaSpinner className="animate-spin text-blue-600 mr-2 text-sm" />
                    <span className="text-blue-700 dark:text-blue-300 text-sm">Uploading file(s)...</span>
                  </div>
                )}
                {uploadError && (
                  <div className="mb-1 p-1.5 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-xs text-center">
                    {uploadError}
                  </div>
                )}

                <div className="w-full flex items-center border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all p-2">

                  {/* File Upload Button - Left side */}
                  <button
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={isLoading || isUploading}
                    className="flex-shrink-0 h-9 w-9 rounded-full transition-all duration-200 flex items-center justify-center shadow-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 z-20"
                    title="Attach files"
                    aria-label="Attach files"
                  >
                    <FaPaperclip className="text-lg" />
                  </button>

                  {/* Textarea Wrapper - Center */}
                  <div className="relative flex-1 flex items-center mx-3 min-h-[24px]">

                    {!inputMessage && (
                      <div className="absolute left-1 right-1 block text-gray-500 dark:text-gray-400 pointer-events-none truncate select-none z-0">
                        Ask me anything about JEE preparation...
                      </div>
                    )}

                    <TextareaAutosize
                      ref={inputRef}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      minRows={1}
                      maxRows={5}
                      className="w-full py-1 px-1 resize-none bg-transparent border-none p-0 focus:ring-0 focus:outline-none text-gray-900 dark:text-white overflow-y-scroll scrollbar-hide relative z-10"
                      onPaste={e => {
                        if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
                          e.preventDefault();
                          handleDirectFileUpload(e.clipboardData.files);
                        }
                      }}
                    />
                  </div>

                  {/* Hidden file input for direct upload */}
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleDirectFileUpload(e.target.files);
                        e.target.value = null;
                      }
                    }}
                    accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.ogg,.m4a"
                  />

                  {/* Send / Stop Button - Right side */}
                  {isLoading ? (
                    <button
                      onClick={() => abortController?.abort("User stopped generation")}
                      className="flex-shrink-0 h-9 w-9 rounded-full transition-all duration-200 flex items-center justify-center shadow-sm z-20 bg-red-100 hover:bg-red-200 text-red-600 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400"
                      title="Stop generating"
                      aria-label="Stop generating"
                    >
                      <FaStopCircle className="text-lg" />
                    </button>
                  ) : (
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || isUploading}
                      className={`flex-shrink-0 h-9 w-9 rounded-full transition-all duration-200 flex items-center justify-center shadow-sm z-20 ${inputMessage.trim() && !isUploading
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        }`}
                      aria-label="Send message"
                    >
                      <FaPaperPlane className="text-base -ml-[1px]" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  Press Enter to send, Shift+Enter for new line • Click 📎 to attach files
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutorComponent;